import React, { useState } from 'react';
import '../App.css';
import axios from 'axios';
import './addInfoButton.css';
import { ChakraProvider } from '@chakra-ui/react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Button,
  Textarea,
} from '@chakra-ui/react';
import { useToast } from '@chakra-ui/react';
import { modelLabel } from '../messageUtils.js';

const AddInfoButton = (props) => {
  const OverlayTwo = () => (
    <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px) hue-rotate(90deg)" />
  );

  const SERVER_URL =
    process.env.NODE_ENV === 'development' ? 'http://localhost:5000/' : '/api/';
  const USER_INFO_SERVER_URL = SERVER_URL + 'submit-user-information';

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [overlay, setOverlay] = React.useState(<OverlayTwo />);
  const [vehicleModel, setVehicleModel] = useState('');
  const [issueSummary, setIssueSummary] = useState('');
  const [repairDetails, setRepairDetails] = useState('');
  const [reason, setReason] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfError, setPdfError] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [imagesError, setImagesError] = useState('');

  const toast = useToast({ position: 'top-right' });

  // Mirrors the server-side policy in feedback_service.py. Client-side checks
  // are UX only — the backend re-validates everything.
  const MAX_PDF_MB = 20;
  const MAX_IMAGE_MB = 10;
  const MAX_IMAGES = 5;

  const handlePdfChange = (event) => {
    const file = event.target.files?.[0] || null;
    setPdfError('');

    if (!file) {
      setPdfFile(null);
      return;
    }

    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (extension !== '.pdf') {
      setPdfError(props.translate('Only PDF files are allowed'));
      setPdfFile(null);
      event.target.value = '';
      return;
    }

    if (file.size > MAX_PDF_MB * 1024 * 1024) {
      setPdfError(`${props.translate('File is too large')} (${MAX_PDF_MB}MB max)`);
      setPdfFile(null);
      event.target.value = '';
      return;
    }

    setPdfFile(file);
  };

  const handleImagesChange = (event) => {
    const files = Array.from(event.target.files || []);
    setImagesError('');

    if (!files.length) {
      setImageFiles([]);
      return;
    }

    if (files.length > MAX_IMAGES) {
      setImagesError(`${props.translate('Too many images')} (${MAX_IMAGES} max)`);
      setImageFiles([]);
      event.target.value = '';
      return;
    }

    const allowedExtensions = ['.png', '.jpg', '.jpeg'];
    for (const file of files) {
      const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
      if (!allowedExtensions.includes(extension)) {
        setImagesError(props.translate('Only PNG and JPG files are allowed'));
        setImageFiles([]);
        event.target.value = '';
        return;
      }
      if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
        setImagesError(`${props.translate('File is too large')} (${MAX_IMAGE_MB}MB max)`);
        setImageFiles([]);
        event.target.value = '';
        return;
      }
    }

    setImageFiles(files);
  };

  const handleSubmit = async () => {
    const missingFields = [
      !vehicleModel && props.translate('Vehicle model'),
      !issueSummary.trim() && props.translate('Issue summary'),
      !repairDetails.trim() && props.translate('Repair details'),
      !reason.trim() && props.translate('Reason'),
    ].filter(Boolean);

    if (missingFields.length) {
      toast({
        title: props.translate('Missing required fields'),
        description: `${props.translate('Please fill in')}: ${missingFields.join(', ')}`,
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (pdfError || imagesError) {
      return;
    }

    const infoPromise = new Promise(async (resolve, reject) => {
      try {
        const formData = new FormData();
        // The backend takes the identity from the verified JWT; this field is
        // informational only and is ignored server-side.
        formData.append('VehicleModel', vehicleModel);
        formData.append('IssueSummary', issueSummary);
        formData.append('RepairDetails', repairDetails);
        formData.append('Reason', reason);
        if (pdfFile) {
          formData.append('PdfFile', pdfFile);
        }
        imageFiles.forEach((file) => formData.append('ImageFiles', file));

        const response = await axios.post(USER_INFO_SERVER_URL, formData);

        if (!response.data.valid) {
          reject(response.data.message || 'Failed to submit information');
        } else {
          resolve('Information submitted successfully');
        }
      } catch (error) {
        reject(error?.response?.data?.message || 'Failed to submit information');
      }
    });

    toast.promise(infoPromise, {
      loading: {
        title: props.translate('Submitting information'),
        description: props.translate('Please wait') + '...',
        duration: 1000,
        isClosable: true,
      },
      success: {
        title: props.translate('Information submitted'),
        description: props.translate('Thank you for your contribution!'),
        duration: 1000,
        isClosable: true,
      },
      error: {
        title: props.translate('Submission failed'),
        description: props.translate('Something went wrong. Please try again.'),
        duration: 1000,
        isClosable: true,
      },
    });

    try {
      await infoPromise;
      setVehicleModel('');
      setIssueSummary('');
      setRepairDetails('');
      setReason('');
      setPdfFile(null);
      setPdfError('');
      setImageFiles([]);
      setImagesError('');
      onClose();
    } catch (error) {
      // Error state is already handled by toast
    }
  };

  return (
    <>
      <button
        className="start-new-btn"
        style={{ fontSize: props.langOpt === 'Japanese' ? '13px' : '12px' }}
        onClick={() => {
          setOverlay(<OverlayTwo />);
          const modelOptions = (props.models || []).slice(0, -2);
          const isCurrentInList = modelOptions.some((m) => m.valueInJap === props.modelFilter);
          setVehicleModel(isCurrentInList ? props.modelFilter : '');
          onOpen();
        }}
      >
        <img
          src="./add.png"
          alt="addInformationIcon"
          style={{ width: '20px', height: '20px' }}
        ></img>
        {props.translate('Post')}
      </button>
      <ChakraProvider resetCSS={false}>
        <Modal isCentered isOpen={isOpen} onClose={onClose}>
          {overlay}
          <ModalContent className="add-info-modal">
            <ModalCloseButton />
            <ModalBody className="add-info-modal-body">
              <h2 className="feedback-title">
                {props.translate('Case study of repair and troubleshooting')}
              </h2>
              <div className="add-info-field">
                <label className="add-info-label" htmlFor="add-info-vehicle-model">
                  {props.translate('Vehicle model')} ({props.translate('Mandatory')})
                </label>
                <select
                  id="add-info-vehicle-model"
                  className="add-info-select"
                  value={vehicleModel}
                  onChange={(e) => setVehicleModel(e.target.value)}
                >
                  <option value="">{props.translate('Select Model')}</option>
                  {((props.models || []).slice(0, -2)).map((model, index) => {
                    const label = modelLabel(model, props.langOpt);
                    return (
                      <option key={index} value={model.valueInJap}>
                        {props.langOpt === 'English' ? `${index + 1}. ` : ''}
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="add-info-field">
                <label className="add-info-label">
                  {props.translate('Issue summary')} ({props.translate('Mandatory')})
                </label>
                <Textarea
                  className="add-info-textarea"
                  placeholder={props.translate('Issue summary')}
                  onChange={(event) => setIssueSummary(event.target.value)}
                  value={issueSummary}
                ></Textarea>
              </div>
              <div className="add-info-field">
                <label className="add-info-label">
                  {props.translate('Repair details')} ({props.translate('Mandatory')})
                </label>
                <Textarea
                  className="add-info-textarea"
                  placeholder={props.translate('Repair details')}
                  onChange={(event) => setRepairDetails(event.target.value)}
                  value={repairDetails}
                ></Textarea>
              </div>
              <div className="add-info-field">
                <label className="add-info-label">
                  {props.translate('Reason')} ({props.translate('Mandatory')})
                </label>
                <Textarea
                  className="add-info-textarea"
                  placeholder={props.translate('Reason')}
                  onChange={(event) => setReason(event.target.value)}
                  value={reason}
                ></Textarea>
              </div>
              <div className="add-info-field">
                <label className="add-info-label">
                  {props.translate('PDF')} ({props.translate('Optional')})
                </label>
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  className="add-info-input"
                  onChange={handlePdfChange}
                />
                {pdfError && (
                  <p className="add-info-note" style={{ color: '#e60000' }}>{pdfError}</p>
                )}
                {pdfFile && !pdfError && (
                  <p className="add-info-note">
                    {pdfFile.name} ({Math.round(pdfFile.size / 1024)} KB)
                  </p>
                )}
              </div>
              <div className="add-info-field">
                <label className="add-info-label">
                  {props.translate('Images')} ({props.translate('Optional')})
                </label>
                <input
                  type="file"
                  accept="image/png,.png,image/jpeg,.jpg,.jpeg"
                  multiple
                  className="add-info-input"
                  onChange={handleImagesChange}
                />
                {imagesError && (
                  <p className="add-info-note" style={{ color: '#e60000' }}>{imagesError}</p>
                )}
                {imageFiles.length > 0 && !imagesError && (
                  <p className="add-info-note">
                    {imageFiles.length} {props.translate('image(s) selected')}: {imageFiles.map((f) => f.name).join(', ')}
                  </p>
                )}
              </div>
              <p className="add-info-note">
                {props.translate('Please describe the reason for this repair in as much detail as possible.')}
              </p>
            </ModalBody>
            <ModalFooter className="add-info-modal-footer">
              <Button onClick={onClose} mr={3} variant="outline">
                {props.translate('Close')}
              </Button>
              <Button
                bg="#e60000"
                color="white"
                _hover={{ bg: '#b80000' }}
                _active={{ bg: '#990000' }}
                onClick={handleSubmit}
              >
                {props.translate('Post')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </ChakraProvider>
    </>
  );
};

export default AddInfoButton;
